<template>
  <div
    class="py-4 container-fluid min-vh-100"
    :class="{ 'profile-page-dark': isDarkMode }"
  >
    <h1 class="profile-page-title mb-4">Profil hráče {{ user?.UserNickName || "" }}</h1>
    <div class="row gy-4">
      <div class="col-lg-4">
        <profile-card
          class="mb-4"
          :user="user"
          @change-image="store.dispatch('openModal', { title: 'Profilová fotka', view: 'CropperImage' })"
          @cancel-account="store.dispatch('openModal', { title: 'Zrušení účtu', view: 'CancelAccount' })"
        />
        <user-settings-card class="mb-4" @open-topup="getPaymentQRCode" />
        <user-accounts-card />
      </div>
      <div class="col-lg-8">
        <transaction-card
          :credit-balance="creditBalance"
          :transaction-data="transactionData"
          @credit-action="creditActionHandler"
        />
      </div>
    </div>
    <base-modal
      v-if="store.state.modal.isOpen && (
        store.state.modal.view === 'CropperImage'
        || store.state.modal.view === 'CancelAccount'
      )"
    >
      <cropper-card
        v-if="store.state.modal.view === 'CropperImage'"
        @cropped-img-data="changeUserImage"
      />
      <cancel-account-card
        v-if="store.state.modal.view === 'CancelAccount'"
      />
    </base-modal>
  </div>
</template>

<script setup>
import { ref, computed, inject, watch } from "vue";
import { useStore } from "vuex";
import dataService from "@/services/dataService";
import ProfileCard from "./components/custom/ProfileCard.vue";
import UserSettingsCard from "./components/custom/UserSettingsCard.vue";
import UserAccountsCard from "./components/custom/UserAccountsCard.vue";
import TransactionCard from "./components/custom/TransactionCard.vue";
import BaseModal from "@/components/BaseModal.vue";
import CropperCard from "./components/custom/CropperCard.vue";
import CancelAccountCard from "./components/custom/CancelAccountCard.vue";
import authService from "../services/authService";

const store = useStore();
const swal = inject("$swal");
const isDarkMode = computed(() => !!store.state.isDarkMode);

const creditBalance = ref(0);
const transactionData = ref([]);

const user = computed(() => store.getters.getUser);
const userID = computed(() => store.getters.getUser?.UserID);
const sessionHash = computed(() => store.getters.isLoggedIn);

const getTransactionData = async () => {
  if (!userID.value || !sessionHash.value) return;
  const creditStatement = await dataService.GetCreditStatement(userID.value, sessionHash.value);
  if (!creditStatement) return;
  creditBalance.value = creditStatement.CreditBalance;
  transactionData.value = creditStatement.TransactionData || [];
};

const getPaymentQRCode = async () => {
  const response = await dataService.GetPaymentQRCode(userID.value, sessionHash.value);

  if (response.Result) {
    store.dispatch("openModal", {
      title: "Dobít kredit",
      view: "TopUpCredit",
      data: response.Data
    });
  } else {
    swal.fire({
      position: "center",
      icon: "error",
      html: `${(response.ErrorMessage || []).map((message) => `<h5>${message}</h5>`).join("")}`,
      showConfirmButton: false,
      timer: 3000
    });
  }
};

const getWithDrawData = async () => {
  const response = await dataService.GetWithDrawData(userID.value, sessionHash.value);

  if (response.Result) {
    store.dispatch("openModal", {
      title: "Vybrat peníze",
      view: "WithdrawCredit",
      data: response.Data
    });
  } else {
    swal.fire({
      position: "center",
      icon: "error",
      html: `${(response.ErrorMessage || []).map((message) => `<h5>${message}</h5>`).join("")}`,
      showConfirmButton: false,
      timer: 3000
    });
  }
};

const creditActionHandler = (action) => {
  if (action === "charge") {
    getPaymentQRCode();
  } else if (action === "withDraw") {
    getWithDrawData();
  }
};

const changeUserImage = async (userImage) => {
  const response = await authService.UserImageChange(userID.value, sessionHash.value, userImage.imgUrl);

  swal.fire({
    position: "center",
    icon: response.Result ? "success" : "error",
    html: response.Result
      ? `${(response.InfoMessage || []).map((message) => `<h5>${message}</h5>`).join("")}`
      : `${(response.ErrorMessage || []).map((message) => `<h5>${message}</h5>`).join("")}`,
    showConfirmButton: false,
    timer: 3000,
    willClose: () => {
      if (response.Result) {
        store.dispatch("updateImageCacheKey");
      }
    }
  });
};

getTransactionData();

watch(() => store.state.creditRefreshKey, () => {
  getTransactionData();
});
</script>

<style scoped>
.profile-page-dark {
  background: #1E293B !important;
}

.profile-page-title {
  font-weight: 800;
  font-size: 1.75rem;
  letter-spacing: -0.02em;
  color: #0F172A;
}

.profile-page-dark .profile-page-title {
  color: #FF8A4C;
}
</style>
